import { theme } from 'antd';
import { invitedStatus } from 'config/invitedStatus';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedUsers } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { LockersAddPeopleForm } from 'zComponents/atoms';
import { Button, Divider, Flex, Modal, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const InvitePeopleModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	// Hooks
	const {
		token: { fontSizeSM, colorTextLabel, paddingXL, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();

	// Redux State
	const selectedUsers = useSelector(selectSelectedUsers);
	const { loadingAddProfile } = useSelector((state) => state.People);

	// ✅ Permissions
	const canAddPerson = useManagePermittedRoles('addPerson');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	// ✅ Local state
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// Helper functions for button logic
	const areAllUsersSuccessfullyInvited = () => {
		return selectedUsers.length > 0 && selectedUsers.every((user) => user.status === invitedStatus.INVITED);
	};

	const areAllUsersAlreadyInvited = () => {
		return selectedUsers.length > 0 && selectedUsers.every((user) => user.status === invitedStatus.INVITED);
	};

	const handleCompleteInvitation = () => {
		setVisible(false);
		handleClearList();
	};

	// Invite People Function
	const handleInvitePeople = async () => {
		dispatch(peopleActions.invitePeople());
	};

	// Clear selected users
	const handleClearList = () => {
		dispatch(peopleActions.clearSelectedUsers());
	};

	// Handle cancel button
	const handleCancel = () => {
		setVisible(false);
		handleClearList();
	};

	return (
		<Fragment>
			<Tooltip
				title={!canAddPerson ? permissionDeniedMessage : 'Invite people to your organisation'}
				placement='top'
				styles={{
					body: {
						textAlign: 'center'
					}
				}}
			>
				<span onClick={(e) => canAddPerson && setVisible(true)}>
					{children ?? (
						<Button type='primary' disabled={!canAddPerson}>
							Invite
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={() => handleCancel()}
				centered
				width={859}
				closeIcon={!loadingAddProfile}
				destroyOnClose
				closable={!loadingAddProfile}
				styles={{
					content: { padding: paddingXL }
				}} //height: 562,
				footer={
					<>
						<div style={{ margin: `0px -${paddingXL}px` }}>
							<Divider style={{ margin: `${paddingXL}px 0` }} />
						</div>
						<Flex align='center' justify='flex-end' gap={paddingSM}>
							<Button onClick={() => handleCancel()} disabled={loadingAddProfile}>
								Cancel
							</Button>
							<Tooltip
								title={
									areAllUsersAlreadyInvited() && !areAllUsersSuccessfullyInvited()
										? 'Selected addresses have already been invited. Please add someone new to invite.'
										: ''
								}
								styles={{
									body: {
										fontSize: fontSizeSM
									}
								}}
							>
								<Button
									type='primary'
									onClick={
										areAllUsersSuccessfullyInvited() ? handleCompleteInvitation : handleInvitePeople
									}
									loading={loadingAddProfile}
									disabled={
										(areAllUsersAlreadyInvited() && !areAllUsersSuccessfullyInvited()) ||
										selectedUsers.length === 0 ||
										loadingAddProfile
									}
								>
									{areAllUsersSuccessfullyInvited() ? 'Complete' : 'Send Invites'}
								</Button>
							</Tooltip>
						</Flex>
					</>
				}
			>
				<Flex vertical>
					{/* Title */}
					<Flex align='start' justify='space-between' vertical style={{ marginBottom: paddingSM }}>
						<Flex vertical>
							<Title level={4} style={{ margin: 0 }}>
								Invite People to Organisation
							</Title>
							<Text style={{ fontSize: fontSizeSM, color: colorTextLabel }}>
								Sending people an email invite gives them access to the Zezamii sign-up link and adds
								them to the Zezamii Platform <br /> as Zezamii Users.
							</Text>
						</Flex>
					</Flex>
					<div style={{ margin: `0px -${paddingXL}px`, marginBottom: paddingXL }}>
						<Divider style={{ margin: '11px 0' }} />
					</div>

					<LockersAddPeopleForm />
				</Flex>
			</Modal>
		</Fragment>
	);
};

export default InvitePeopleModal;
